import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';

export const CONSENT_KEY = 'accesshub_cookie_preferences';
export const CONSENT_VERSION = 2;
export interface CookiePreferences {
  version: number;
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  advertising: boolean;
  updatedAt: string;
}

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  readonly ready = signal(false);
  readonly hasChoice = signal(false);
  readonly storageAvailable = signal(true);
  readonly preferences = signal<CookiePreferences>(this.defaults());

  init(): void {
    if (!isPlatformBrowser(this.platformId) || this.ready()) return;
    this.read();
    this.ready.set(true);
    this.cleanup();
    this.document.defaultView?.addEventListener('storage', (event) => {
      if (event.key !== CONSENT_KEY && event.key !== null) return;
      const hadAnalytics = this.preferences().analytics;
      this.read();
      this.cleanup();
      if (hadAnalytics && !this.preferences().analytics)
        this.document.defaultView?.location.reload();
    });
  }

  allows(category: 'analytics' | 'marketing' | 'advertising'): boolean {
    return this.ready() && this.hasChoice() && this.preferences()[category];
  }

  save(analytics: boolean, marketing: boolean, advertising = false): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    // The ad SDK service unloads its own document when needed. Do not reload
    // checkout/auth pages merely because an ad choice changed in another tab.
    const reloadRequired = this.allows('analytics') && !analytics;
    const next: CookiePreferences = {
      ...this.defaults(),
      analytics,
      marketing,
      advertising,
      updatedAt: new Date().toISOString(),
    };
    try {
      this.document.defaultView?.localStorage.setItem(
        CONSENT_KEY,
        JSON.stringify(next)
      );
      this.storageAvailable.set(true);
    } catch {
      this.storageAvailable.set(false);
    }
    this.preferences.set(next);
    this.hasChoice.set(true);
    this.ready.set(true);
    this.cleanup();
    return reloadRequired;
  }

  private defaults(): CookiePreferences {
    return {
      version: CONSENT_VERSION,
      necessary: true,
      analytics: false,
      marketing: false,
      advertising: false,
      updatedAt: '',
    };
  }

  private read(): void {
    try {
      const stored = JSON.parse(
        this.document.defaultView?.localStorage.getItem(CONSENT_KEY) || 'null'
      );
      const valid =
        (stored?.version === CONSENT_VERSION || stored?.version === 1) &&
        stored?.necessary === true &&
        typeof stored.analytics === 'boolean' &&
        typeof stored.marketing === 'boolean' &&
        (stored.version === 1
          ? stored.advertising === false
          : typeof stored.advertising === 'boolean') &&
        typeof stored.updatedAt === 'string' &&
        Number.isFinite(Date.parse(stored.updatedAt));
      // Preserve existing analytics/marketing choices, never infer ad consent.
      this.preferences.set(
        valid
          ? {
              ...stored,
              version: CONSENT_VERSION,
              advertising: stored.version === 1 ? false : stored.advertising,
            }
          : this.defaults()
      );
      this.hasChoice.set(!!valid);
    } catch {
      this.preferences.set(this.defaults());
      this.hasChoice.set(false);
      this.storageAvailable.set(false);
    }
  }

  private cleanup(): void {
    const win = this.document.defaultView;
    if (!win) return;
    try {
      if (!this.preferences().analytics && !this.preferences().marketing) {
        for (let index = win.localStorage.length - 1; index >= 0; index--) {
          const key = win.localStorage.key(index);
          if (key?.startsWith('accesshub.funnel-session-id:'))
            win.localStorage.removeItem(key);
        }
      }
      if (!this.preferences().analytics)
        win.sessionStorage.removeItem('ah_tracked_purchases');
    } catch {
      this.storageAvailable.set(false);
    }
    if (this.preferences().analytics) return;
    try {
      this.clearAnalyticsCookies(win);
    } catch {
      // Restricted browser contexts must not break essential navigation.
      this.storageAvailable.set(false);
    }
  }

  private clearAnalyticsCookies(win: Window): void {
    const names = this.document.cookie
      .split(';')
      .map((cookie) => cookie.split('=')[0].trim())
      .filter((name) => /^_ga(?:_|$)|^_gid$|^_gat(?:_|$)/.test(name));
    const hostParts = win.location.hostname.split('.');
    const domains = [
      '',
      ...hostParts
        .slice(0, -1)
        .map((_, index) => '.' + hostParts.slice(index).join('.')),
    ];
    for (const name of names)
      for (const domain of domains) {
        this.document.cookie = `${name}=; Max-Age=0; path=/;${
          domain ? ' domain=' + domain + ';' : ''
        }`;
      }
  }
}
